import type { SubskillDetail } from '../../api/models';
import { AppText } from '../../components/AppText';
import { RetryNotice } from '../../components/RetryNotice';
import { Strings } from '../../strings';

import { useSkillDetail } from './useSkillDetail';

const MAXIMUM_LEVEL = 10;

/**
 * design-system/08-Screens.md §6 — one skill, broken into the sub-skills the engine actually tracks.
 * Mirrors android/…/Screens/SkillDetail/SkillDetailScreen.kt.
 *
 * A sub-skill with no closed attempts shows "zatiaľ bez údajov" rather than 0 %. Zero would read as a
 * failure when it means nobody has asked yet, and that is exactly the kind of accidental judgement the
 * product exists to avoid.
 */
export function SkillDetailScreen({
  childId,
  skillCode,
}: {
  childId: string | null;
  skillCode: string | null;
}) {
  const model = useSkillDetail(childId, skillCode);
  const detail = model.state.detail;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      {model.state.error !== null ? (
        <RetryNotice
          text={model.state.error}
          correlationId={model.state.errorCorrelationId}
          tone="error"
          onRetry={() => void model.load()}
        />
      ) : null}

      {detail !== null ? (
        <>
          <AppText as="h1" style="titleL">
            {detail.skillName}
          </AppText>

          <AppText color="var(--text-secondary)">{Strings.skillLevel(detail.level, MAXIMUM_LEVEL)}</AppText>

          {(detail.subskills ?? []).map((subskill: SubskillDetail) => (
            <SubskillRow key={subskill.subskillCode} subskill={subskill} />
          ))}
        </>
      ) : null}
    </div>
  );
}

function SubskillRow({ subskill }: { subskill: SubskillDetail }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-3)',
        borderRadius: 'var(--radius-m)',
        background: 'var(--surface-raised)',
        padding: 'var(--space-4)',
      }}
    >
      <div style={{ flex: 1 }}>
        <AppText>{subskill.subskillName}</AppText>
        <AppText style="bodyS" color="var(--text-secondary)">
          {subskill.closedInstanceCount === 0
            ? Strings.skillNoData
            : Strings.skillAccuracy(Math.round(subskill.accuracy * 100))}
        </AppText>
      </div>

      <AppText style="numeric" color="var(--action-primary)">
        {subskill.level}
      </AppText>
    </div>
  );
}
